import {
    EventInteraction,
    EventInteractionContext,
    usePlacementEventContextAggregator,
} from '@eventbrite/event-tracker';
import * as analytics from '@eventbrite/site-analytics';
import { StatsigOptions, useLayer } from '@eventbrite/statsig';
import { $FixMe } from '@eventbrite/ts-utils';
import loadable from '@loadable/component';
import classNames from 'classnames';
import has from 'lodash/has';
import isEmpty from 'lodash/isEmpty';
import React, { useRef } from 'react';
import { connect } from 'react-redux';
import { WaypointLazyRender } from '../../../../components/WaypointLazyRender';
import {
    ARTICLE_TEMPLATE,
    CARD_BIG,
    CARD_TEMPLATE_4,
    CARD_TEMPLATE_8,
    CATEGORY_TEMPLATE,
    COLLECTION_TEMPLATE,
    ENTITY_TYPE_MAP,
    EVENT_TIME_OPTIONS_TEMPLATE,
    FEATURED_TEMPLATE,
    LARGE_CARD_TEMPLATE_3,
    LARGE_CARD_TEMPLATE_6,
    NEARBY_CITIES_TEMPLATE,
    ORGANIZER_FOLLOW_TEMPLATE,
    TRENDING_SEARCHES_TEMPLATE,
} from '../../../../constants';
import {
    CITY_BROWSE_EVENT_VIEW_AFFILIATE,
    GA_CITY_BROWSE_CATEGORY,
    ONLINE_BROWSE_EVENT_VIEW_AFFILIATE,
} from '../../../../constants/analytics';
import { LINK_TO_ALL_EVENTS_PAGE_2_EXPERIMENT } from '../../../../constants/constants';
import { trackEventClickInternal as trackEventClickInternalAction } from '../../../../redux/actions/analytics';
import { trackLocationChange as trackLocationChangeAction } from '../../../../redux/actions/location';
import { Event, Events, FeatureFlags } from '../../../../types/index';
import { formatBucketLabelFromKey } from '../../../../utils/buckets';
import { getEventFromId } from '../../../../utils/general';
import {
    useBrowseEventExpansions,
    useBrowseEventExpansionsProps,
} from './expansionUtils';

const CollectionsBucket = loadable(
    () => import('../../../../components/FeaturedCollections'),
    { resolveComponent: (components: any) => components.CollectionsBucket },
);

const CardBucket = loadable(() => import('../../../../components/CardBucket'), {
    resolveComponent: (components: { CardBucket: React.ReactElement }) =>
        components.CardBucket,
});

const OrganizerFollowBucket = loadable(
    () => import('../OrganizerFollowBucket'),
    {
        resolveComponent: (components: {
            OrganizerFollowBucket: React.ReactElement;
        }) => components.OrganizerFollowBucket,
    },
);
const NearbyCitiesBucket = loadable(
    () => import('../../components/NearbyCitiesBucket'),
    {
        resolveComponent: (components: {
            NearbyCitiesBucket: React.ReactElement;
        }) => components.NearbyCitiesBucket,
    },
);
const TrendingSearchesBucket = loadable(
    () => import('../../components/TrendingSearchesBucket'),
    {
        resolveComponent: (components: {
            TrendingSearchesBucket: React.ReactElement;
        }) => components.TrendingSearchesBucket,
    },
);
const CategoryBucket = loadable(
    () => import('../../components/CategoryBucket'),
    {
        resolveComponent: (components: {
            CategoryBucket: React.ReactElement;
        }) => components.CategoryBucket,
    },
);
const EventTimeOptionsBucket = loadable(
    () => import('../../components/EventTimeOptionsBucket'),
    {
        resolveComponent: (components: {
            EventTimeOptionsBucket: React.ReactElement;
        }) => components.EventTimeOptionsBucket,
    },
);

const { trackEventFromState, trackEventFromStateOnPageUnload } = analytics;

export const BUCKET_LOOKUP_MAP: { [key: string]: React.ReactElement } = {
    [FEATURED_TEMPLATE]: CardBucket,
    [LARGE_CARD_TEMPLATE_3]: CardBucket,
    [LARGE_CARD_TEMPLATE_6]: CardBucket,
    [CARD_TEMPLATE_4]: CardBucket,
    [CARD_TEMPLATE_8]: CardBucket,
    [CARD_BIG]: CardBucket,
    [CATEGORY_TEMPLATE]: CategoryBucket,
    [NEARBY_CITIES_TEMPLATE]: NearbyCitiesBucket,
    [ARTICLE_TEMPLATE]: CardBucket,
    [TRENDING_SEARCHES_TEMPLATE]: TrendingSearchesBucket,
    [COLLECTION_TEMPLATE]: CollectionsBucket,
    [ORGANIZER_FOLLOW_TEMPLATE]: OrganizerFollowBucket,
    [EVENT_TIME_OPTIONS_TEMPLATE]: EventTimeOptionsBucket,
};

const DecorativeImage = ({
    decorationPlacement,
    decorativeImage,
}: Pick<
    useBrowseEventExpansionsProps,
    'decorationPlacement' | 'decorativeImage'
>) => {
    let component = null;

    if (decorativeImage) {
        const classes = classNames(
            'decorative-image',
            `decorative-image--${decorationPlacement?.position}`,
            decorationPlacement?.classes,
        );
        const parentClasses = classNames(
            'decorative-image--container',
            decorationPlacement?.parentClasses,
        );

        component = (
            <div className={parentClasses}>
                <img className={classes} src={decorativeImage} alt="" />
            </div>
        );
    }

    return component;
};

const shouldReturnEmpty = (content: any, type: string, template: string) => {
    /*Future proofing such that if api adds additional buckets app doesn't break*/
    /*
    If content is undefined, that means the bucket type map has the appropriate key,
    but the value associated to it necessary to access the actual content in the response
    has changed. Solution is we should send an error, but not let the app fall over.
    */
    if (
        (!content || isEmpty(content)) &&
        template !== EVENT_TIME_OPTIONS_TEMPLATE
    ) {
        return true;
    }

    /* If the passed in type is not known in the ENTITY_TYPE_MAP this means that the API has
    started to return a layout format that is not known. The API should be allowed to progress
    without causing errors so no error should be fired. */
    if (!has(ENTITY_TYPE_MAP, type)) {
        return true;
    }

    /* If the passed in template is not known in the BUCKET_LOOKUP_MAP this means that the API has
    started to return a layout format that is not known. The API should be allowed to progress
    without causing errors so no error should be fired. */
    if (!has(BUCKET_LOOKUP_MAP, template)) {
        return true;
    }

    return false;
};

const EmptyBucket = (props: { type: string }) => {
    return <div key={props.type} className="empty-bucket" />;
};

const CITY_BROWSE_PLACEMENT_NAME = 'city_browse';

function aggregateContext({
    id,
    event: eventData,
    events = { promoted: [], organic: [] },
    location,
    user,
    bucketType,
    bucketIndex,
    locale,
    searchId,
}: Omit<EventInteraction, 'action'> & {
    events: { promoted: Events; organic: Events };
    location: { isOnline?: boolean; placeId?: string };
    user: { id?: string; guestId?: string; sessionId?: string };
    bucketType?: string;
    bucketIndex?: number;
    locale?: string;
    searchId?: string;
}): EventInteractionContext {
    const { event, absolutePosition } = getEventFromId(
        events,
        id,
        eventData.isPromoted,
    );
    return {
        user: {
            id: user.id,
            locale: locale,
            guestId: user.guestId,
            sessionId: user.sessionId,
        },
        adId: event?.promotedListingMetadata?.adId,
        place: {
            name: CITY_BROWSE_PLACEMENT_NAME,
            position: absolutePosition,
            page: 1,
            context: {
                place_id: location.placeId,
                is_online: location.isOnline,
                search_id: searchId,
                latitude: null,
                longitude: null,
            },
            subInterface: {
                name: bucketType,
                position:
                    bucketIndex != undefined ? `${bucketIndex + 1}` : undefined,
            },
            placementId: event?.promotedListingMetadata?.placementId,
        },
        venueId: event?.venue?.id,
    };
}

interface BrowseBucketProps {
    onEnter?: Function;
    allEventsUrl?: string;
    bucketIndex?: number;
    bucketKey: string;
    keyMin?: string;
    bucketWrapperClasses?: string;
    currentPlace?: string;
    containerClasses?: string;
    content?: object[];
    decorationPlacement?: {
        position: string;
        classes: string[];
        parentClasses: string[];
    };
    decorativeImage?: string;
    httpReferer?: string;
    isAuthenticated?: boolean;
    linkUrl?: string;
    locationSlug?: string;
    name: string;
    onTrackEventClickInternal?: Function;
    template?: string;
    trackLocationChange?: Function;
    trackGAEvent?: Function;
    trackGAEventOnPageUnload?: Function;
    type?: string;
    seeMoreMarkedNoFollow?: boolean;
    isGDPRCountry?: boolean;
    featureFlags?: FeatureFlags;
    isBotRequest?: boolean;
    trackEventClickInternal?: Function;
    seeMore?: {
        url?: string;
        params?: { dates?: string[]; places?: string[]; tags?: string[] };
    };
    inWindow?: boolean;
    isOnline?: boolean;
    userId?: string;
    guestId?: string;
    placeId?: string;
    searchId?: string;
    sessionId?: string;
    options?: StatsigOptions;
    locale: string;
}

export const BrowseBucket = (props: BrowseBucketProps) => {
    const content = (props.content || []) as Event[];
    let { url: linkUrl } = props.seeMore || { url: '' };

    const { value } = useLayer(
        LINK_TO_ALL_EVENTS_PAGE_2_EXPERIMENT.layer,
        LINK_TO_ALL_EVENTS_PAGE_2_EXPERIMENT.param,
        LINK_TO_ALL_EVENTS_PAGE_2_EXPERIMENT.defaultValue,
    );

    if (linkUrl?.includes('all-events') && value) {
        linkUrl += '?page=2';
    }

    const isEmptyBucket = shouldReturnEmpty(
        props.content,
        props.type || '',
        props.template || '',
    );

    //Expand events in this bucket to get additional
    //data that we don't parse on the server
    useBrowseEventExpansions({
        ...props,
        isEmptyBucket,
        inWindow: props.inWindow || false,
    });

    const domElement = useRef(null);
    const promotedEvents = content.filter(
        (event: Event) => event?.promotedListingMetadata,
    );
    const organicEvents = content.filter(
        (event: Event) => !event?.promotedListingMetadata,
    );

    usePlacementEventContextAggregator(domElement, ({ id, event }) =>
        aggregateContext({
            id,
            event,
            events: { promoted: promotedEvents, organic: organicEvents },
            location: {
                isOnline: props.isOnline,
                placeId: props.placeId,
            },
            user: {
                id: props.userId,
                guestId: props.guestId,
                sessionId: props.sessionId,
            },
            bucketType: props.bucketKey,
            bucketIndex: props.bucketIndex,
            locale: props.locale,
            searchId: props.searchId,
        }),
    );

    if (isEmptyBucket && props.template !== 'organizer_follow') {
        return <EmptyBucket type={props.type || ''} />;
    }

    const affCode = props.isOnline
        ? ONLINE_BROWSE_EVENT_VIEW_AFFILIATE
        : CITY_BROWSE_EVENT_VIEW_AFFILIATE;

    const DynamicBucket = BUCKET_LOOKUP_MAP[props.template || ''] as any;
    const bucketLabel = formatBucketLabelFromKey({
        bucketKey: props.bucketKey,
        tabKey: '',
        name: props.name,
        currentPlace: props?.currentPlace || '',
    });

    return (
        <div
            ref={domElement}
            className={props.containerClasses}
            key={props.bucketKey}
        >
            <div
                className={props.bucketWrapperClasses}
                data-event-bucket-label={bucketLabel}
            >
                <DecorativeImage
                    decorationPlacement={props.decorationPlacement}
                    decorativeImage={props.decorativeImage}
                />
                <DynamicBucket
                    linkUrl={linkUrl}
                    onTrackEventClickInternal={(data: any) =>
                        props.trackEventClickInternal?.(data)
                    }
                    affCode={affCode}
                    {...props}
                />
            </div>
        </div>
    );
};

const LazyRenderBrowseBucket: React.FunctionComponent<BrowseBucketProps> = (
    props,
) => {
    const { bucketIndex, onEnter } = props;
    const [inWindow, setIsInWindow] = React.useState(false);

    const handleEnter = () => {
        setIsInWindow(true);
        onEnter?.();
    };

    return (
        <WaypointLazyRender
            initialIsVisible={
                props.isBotRequest ||
                (typeof bucketIndex === 'number' && bucketIndex <= 3)
            }
            emptyStateStyles={{ height: '300px' }}
            onEnter={handleEnter}
        >
            <BrowseBucket {...props} inWindow={inWindow} />
        </WaypointLazyRender>
    );
};

export const _mapStateToProps = (
    state: {
        app: Pick<
            useBrowseEventExpansionsProps,
            | 'httpReferer'
            | 'featureFlags'
            | 'isBotRequest'
            | 'isGDPRCountry'
            | 'options'
        > & {
            domain?: string;
            experiments?: any;
            gaSettings?: object;
            publicId?: string;
            tld?: string;
            userEmail?: string;
            guestId?: string;
            sessionId?: string;
            locale: string;
        };
        location: Pick<
            useBrowseEventExpansionsProps,
            'currentPlace' | 'placeId' | 'isOnline'
        > & {
            currentPlaceParent?: string;
            placeType?: string;
            slug?: string;
        };
        auth: Pick<useBrowseEventExpansionsProps, 'isAuthenticated'>;
        content: Pick<useBrowseEventExpansionsProps, 'allEventsUrl'>;
        search: {
            searchId?: string;
        };
    },
    ownProps: Pick<
        useBrowseEventExpansionsProps,
        | 'containerClasses'
        | 'bucketWrapperClasses'
        | 'decorationPlacement'
        | 'decorativeImage'
    >,
) => {
    const {
        containerClasses,
        bucketWrapperClasses,
        decorationPlacement,
        decorativeImage,
    } = ownProps;

    return {
        tld: state.app.tld,
        containerClasses,
        bucketWrapperClasses,
        decorationPlacement,
        decorativeImage,
        currentPlace: state.location.currentPlace,
        currentPlaceParent: state.location.currentPlaceParent,
        placeType: state.location.placeType,
        isAuthenticated: state.auth.isAuthenticated,
        allEventsUrl: state.content.allEventsUrl,
        httpReferer: state.app.httpReferer,
        locationSlug: state.location.slug,
        domain: state.app.domain,
        locale: state.app.locale,
        featureFlags: state.app.featureFlags,
        experiments: state.app.experiments,
        userEmail: state.app.userEmail,
        publicId: state.app.publicId,
        userId: state.app.publicId,
        guestId: state.app.guestId,
        placeId: state.location.placeId,
        isOnline: state.location.isOnline,
        isBotRequest: state.app.isBotRequest,
        isGDPRCountry: state.app.isGDPRCountry,
        gaCategory: GA_CITY_BROWSE_CATEGORY,
        gaActionSuffix: 'CityBrowse',
        gaSettings: state.app.gaSettings,
        searchId: state.search.searchId,
        sessionId: state.app.sessionId,
        options: state.app.options,
        trackGAEvent: (action: any) =>
            trackEventFromState(state, {
                category: GA_CITY_BROWSE_CATEGORY,
                ...action,
            }),
        trackGAEventOnPageUnload: (action: any) =>
            trackEventFromStateOnPageUnload(state, {
                category: GA_CITY_BROWSE_CATEGORY,
                ...action,
            }),
    };
};
const _mapDispatchToProps = (dispatch: Function) => ({
    trackLocationChange: (...args: [$FixMe]) =>
        dispatch(trackLocationChangeAction(...args)),
    trackEventClickInternal: (...args: [$FixMe]) =>
        dispatch(trackEventClickInternalAction(...args)),
});

export const BrowseBucketContainer = connect(
    _mapStateToProps,
    _mapDispatchToProps,
)(LazyRenderBrowseBucket);

export default BrowseBucketContainer;
